/** Keep in sync with dev-servers-registry.mjs */
export const DEV_SERVER_GROUP_ORDER = [
  'shell',
  'journey',
  'marketing',
  'tools',
  'engines',
  'apis',
  'archived',
  'platform',
] as const;

export type DevServerGroup = (typeof DEV_SERVER_GROUP_ORDER)[number];

export const DEV_SERVER_GROUP_LABELS: Record<DevServerGroup, string> = {
  shell: 'Product shell',
  journey: 'Journey apps',
  marketing: 'Marketing & sites',
  tools: 'Dev tools & UIs',
  engines: 'Engines',
  apis: 'APIs & root scripts',
  archived: 'Archived (legacy/)',
  platform: 'Platform services',
};

export const DEV_SERVER_IDS = [
  'mainSpa',
  'peakLlm',
  'whatsnext',
  'leaderOnboarding',
  'parachute',
  'docsSite',
  'marketingSite',
  'peakPrioritiesSite',
  'siteBuilderDirect',
  'digitalWorkbook',
  'orchestrationUi',
  'partnerCapabilityUi',
  'objectInspector',
  'ideaGeneratorFrontend',
  'prototypeServer',
  'claritas',
  'prospectorEngine',
  'knowledgeEngine',
  'orchestrationEngine',
  'authoringEngine',
  'studioEngine',
  'consistencyEngine',
  'docsServer',
  'assemblyEngine',
  'publishingEngine',
  'integrationsEngine',
  'curationEngine',
  'canonApi',
  'peakApi',
  'partnerCapabilityApi',
  'ideaGeneratorBackend',
  'ticktickSync',
  'backplanApp',
  'simpleWeightedScoring',
  'pairwiseRanking',
  'archivedUserApp',
  'mongoLocal',
  'supabaseLocal',
  'visualLanguageServer',
] as const;

export type DevServerId = (typeof DEV_SERVER_IDS)[number];

export function inferDevServerId(path: string): DevServerId | undefined {
  if (path.includes('localhost:3150') || path.startsWith('/site-configurator')) {
    if (path.includes('site-configurator')) return 'siteBuilderDirect';
  }
  if (path.includes('localhost:3201')) return 'whatsnext';
  if (path.includes('localhost:3202')) return 'leaderOnboarding';
  if (path.includes('localhost:3203')) return 'parachute';
  if (path.includes('/peak-home')) return 'mainSpa';
  if (path.includes('localhost:4322')) return 'peakPrioritiesSite';
  if (path.includes('localhost:4321')) return 'marketingSite';
  if (path.includes('localhost:5174')) return 'siteBuilderDirect';
  if (path.includes('localhost:3000')) return 'docsSite';
  if (path.includes('localhost:4008')) return 'docsServer';
  if (path.includes('localhost:3004')) return 'peakLlm';
  if (path.includes('localhost:3400') || path.includes('localhost:3102')) return 'objectInspector';
  if (path.includes('localhost:3103')) return 'orchestrationUi';
  if (path.includes('localhost:3210')) return 'visualLanguageServer';
  if (path.includes('localhost:3220')) return 'claritas';
  if (path.includes('localhost:3101')) return 'digitalWorkbook';
  if (path.includes('localhost:3127')) return 'partnerCapabilityUi';
  if (path.includes('localhost:4001')) return 'prospectorEngine';
  if (path.includes('localhost:4013')) return 'canonApi';
  if (path.includes('localhost:4004')) return 'orchestrationEngine';
  if (path.includes('localhost:4100')) return 'integrationsEngine';
  if (path.includes('localhost:4110')) return 'peakApi';
  if (path.includes('localhost:7331')) return 'prototypeServer';
  if (path.includes('localhost:5173')) return 'ideaGeneratorFrontend';
  if (path.includes('localhost:3189')) return 'partnerCapabilityApi';
  if (path.includes('localhost:3001')) return 'ideaGeneratorBackend';
  if (path.includes('localhost:3003')) return 'ticktickSync';
  return undefined;
}

/**
 * Which local dev server must be running for this site-map entry to work in dev.
 * Returns undefined for repo-only paths and external cloud URLs (Notion, n8n, etc.).
 */
export function resolveSiteMapDevServer(page: {
  path: string;
  devServerId?: DevServerId;
  repoOnly?: boolean;
}): DevServerId | undefined {
  if (page.repoOnly) return undefined;
  if (page.devServerId) return page.devServerId;

  const fromUrl = inferDevServerId(page.path);
  if (fromUrl) return fromUrl;

  // Proxied through main SPA — page shell loads on :3150 but needs the backend.
  if (page.path.includes('site-configurator')) return 'siteBuilderDirect';
  if (page.path.includes('/peak-home')) return 'mainSpa';
  if (page.path.startsWith('/wiki') || page.path.includes('/wiki/')) return 'docsServer';

  // In-app routes that call a specific engine API.
  if (page.path === '/batch-process-images') return 'prospectorEngine';

  // Default: same-origin React routes (including /visual-language/ via Vite plugin).
  if (page.path.startsWith('/') || page.path.startsWith(import.meta.env.BASE_URL)) {
    return 'mainSpa';
  }

  // External https (Notion, webhooks, etc.) — no local dev server to watch.
  return undefined;
}

export type DevServerStatus = {
  id: DevServerId;
  label: string;
  port: number;
  cwd: string;
  cwdExists: boolean;
  running: boolean;
  starting: boolean;
  group?: DevServerGroup;
  statusOnly?: boolean;
  noStart?: boolean;
  archived?: boolean;
  conflictNote?: string;
  nodeModules?: boolean;
  lastError?: string;
};

export type DevServersSnapshot = {
  available: boolean;
  servers: Partial<Record<DevServerId, DevServerStatus>>;
};

/** Map section headers to a dev server when every page in the column shares one port. */
export const SECTION_DEV_SERVER: Partial<Record<string, DevServerId>> = {
  Core: 'mainSpa',
  'Peak Site': 'peakPrioritiesSite',
  Marketing: 'marketingSite',
  Publishing: 'siteBuilderDirect',
  'Elevation Platform': 'mainSpa',
  'Priorities.ai Product': 'mainSpa',
  'Alternate Product UIs': 'mainSpa',
  'MOF Architecture Layers': 'mainSpa',
  'Design System (Elevation)': 'mainSpa',
  'Learning & Exploration': 'mainSpa',
  'Diagram Engine': 'mainSpa',
};
